import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Button,
  Skeleton,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Download as DownloadIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import DateRangePicker from './DateRangePicker';
import MetricCard from './MetricCard';
import { 
  fetchLandingPagesAnalytics,
  DateRange,
  LandingPagesData
} from '../../services/analyticsService';
import { format, eachDayOfInterval, parseISO, startOfWeek, startOfMonth } from 'date-fns';
import { useUser } from '../../contexts/UserContext';

interface Props {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  groupId?: number;
  clientId?: number | null;
}

interface PageDetails {
  pagePath: string;
  totalViews: number;
  uniqueUsers: number;
  newUsers: number;
  avgEngagementTime: number;
  bounceRate: number;
  engagedSessions: number;
  eventCount: number;
}

interface SortConfig {
  key: keyof PageDetails;
  direction: 'asc' | 'desc';
}

const LandingPages: React.FC<Props> = ({ dateRange, onDateRangeChange, groupId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LandingPagesData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'totalViews',
    direction: 'desc'
  });
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const { selectedClientId } = useUser();
  const [showPages, setShowPages] = useState<string[]>([]);
  
  // Use provided clientId if available, otherwise fall back to selectedClientId
  const effectiveClientId = clientId || selectedClientId;

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveClientId && !groupId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const params: any = {
          date_range: dateRange.date_range,
          start_date: dateRange.startDate?.toISOString(),
          end_date: dateRange.endDate?.toISOString()
        };
        
        if (groupId) {
          params.group_id = groupId;
          if (clientId) {
            params.client_id = clientId;
          }
        } else {
          params.client_id = effectiveClientId;
        }
        
        const response = await fetchLandingPagesAnalytics(params);
        setData(response);
      } catch (err: any) {
        console.error('Error fetching landing pages analytics:', err);
        setError(err.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, effectiveClientId, groupId, clientId]);

  useEffect(() => {
    if (data && data.pages) {
      setShowPages(data.pages.map((p) => p.pagePath));
    }
  }, [data]);

  const handleSort = (key: keyof PageDetails) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleExport = () => {
    if (!data) return;

    const headers = [
      'Page Path',
      'Total Views',
      'Unique Users',
      'New Users',
      'Avg. Engagement Time',
      'Bounce Rate',
      'Engaged Sessions',
      'Event Count'
    ];

    const csvContent = [
      headers.join(','),
      ...data.pages.map((page: PageDetails) => [
        page.pagePath,
        page.totalViews,
        page.uniqueUsers,
        page.newUsers,
        page.avgEngagementTime,
        page.bounceRate,
        page.engagedSessions,
        page.eventCount
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `landing-pages-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredPages = data?.pages.filter(page => 
    page.pagePath.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const sortedPages = [...filteredPages].sort((a, b) => {
    if (sortConfig.direction === 'asc') {
      return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
    }
    return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
  });

  const handleTogglePage = (pagePath: string) => {
    setShowPages((prev) =>
      prev.includes(pagePath)
        ? prev.filter((p) => p !== pagePath)
        : [...prev, pagePath]
    );
  };
  const allShown = data && data.pages && showPages.length === data.pages.length;
  const handleToggleAll = () => {
    if (allShown) {
      setShowPages([]);
    } else if (data && data.pages) {
      setShowPages(data.pages.map((p) => p.pagePath));
    }
  };

  if (error) {
    return (
      <Box className="p-4">
        <Alert severity="error" className="mb-4">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => onDateRangeChange({ date_range: 'last_30_days' })}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" className="mb-6">
        <DateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
      </Box>
      <Grid container spacing={3} className="mt-4">
        {/* Top Metrics */}
        <Grid item xs={12} sm={6} md={3} component="div">
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title="Total Page Views"
              value={data?.totalPageViews || 0}
              description="Total number of page views during the selected period"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} component="div">
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title="Unique Users"
              value={data?.totalUniqueUsers || 0}
              description="Number of unique users who visited the pages. Note: We don't currently relate landing page users to portal users. A future release will add the ability to see these users."
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} component="div">
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title="Avg. Engagement Time"
              value={data?.averageEngagementTime || 0}
              description="Average time users spent on pages"
              isPercentage={false}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} component="div">
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title="Bounce Rate"
              value={data ? Math.round(data.averageBounceRate * 100) : 0}
              description="Percentage of users who left without interaction"
              isPercentage={true}
            />
          )}
        </Grid>

        {/* Page Views Trend */}
        <Grid item xs={12} component="div">
          <Paper elevation={0} className="p-4 border border-gray-100">
            <Typography variant="h6" className="mb-4 text-gray-800 font-medium">
              Page Views Over Time
            </Typography>
            <Box className="h-[300px]">
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                data && data.pageViewsByDay.length > 0 && showPages.length > 0 ? (
                  (() => {
                    // Helper functions to match the overview implementation
                    const createDailyData = (startDate: Date, endDate: Date, viewsMap: Map<string, number>) => {
                      const days = eachDayOfInterval({ start: startDate, end: endDate });
                      return days.map(date => ({
                        x: format(date, 'MMM d'),
                        y: viewsMap.get(format(date, 'yyyy-MM-dd')) || 0,
                        originalDate: format(date, 'yyyy-MM-dd')
                      }));
                    };

                    const createWeeklyRollups = (startDate: Date, endDate: Date, viewsMap: Map<string, number>) => {
                      const rollups = [];
                      let current = new Date(startDate);
                      current = startOfWeek(current, { weekStartsOn: 1 });
                      
                      while (current <= endDate) {
                        const weekStart = current;
                        let weekEnd = new Date(current);
                        weekEnd.setDate(weekEnd.getDate() + 6);
                        if (weekEnd > endDate) weekEnd = endDate;
                        
                        const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
                        let sum_views = 0;
                        days.forEach(day => {
                          const dateKey = format(day, 'yyyy-MM-dd');
                          sum_views += viewsMap.get(dateKey) || 0;
                        });
                        
                        rollups.push({
                          x: format(weekEnd, 'MMM d'),
                          y: sum_views,
                          originalDate: format(weekEnd, 'yyyy-MM-dd')
                        });
                        current = new Date(weekEnd);
                        current.setDate(current.getDate() + 1);
                      }
                      return rollups;
                    };

                    const createMonthlyRollups = (startDate: Date, endDate: Date, viewsMap: Map<string, number>) => {
                      const rollups = [];
                      let current = new Date(startDate);
                      current.setDate(1); // Start from beginning of month
                      
                      // Generate all months from start to end, including current month
                      while (current <= endDate) {
                        const monthStart = current > startDate ? current : startDate;
                        let monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
                        if (monthEnd > endDate) monthEnd = endDate;
                        
                        const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                        let sum_views = 0;
                        days.forEach(day => {
                          const dateKey = format(day, 'yyyy-MM-dd');
                          sum_views += viewsMap.get(dateKey) || 0;
                        });
                        
                        rollups.push({
                          x: format(current, 'MMM yyyy'),
                          y: sum_views,
                          originalDate: format(current, 'yyyy-MM')
                        });
                        current.setMonth(current.getMonth() + 1);
                      }
                      return rollups;
                    };

                    // Build a map of daily data (same as overview)
                    const viewsMap = new Map<string, number>();
                    data.pageViewsByDay.forEach(d => {
                      if (showPages.includes(d.pagePath)) {
                        viewsMap.set(d.date, (viewsMap.get(d.date) || 0) + d.views);
                      }
                    });

                    // Calculate total days in the range (same as overview)
                    const startDate = new Date(dateRange.startDate || '2000-01-01');
                    const endDate = new Date(dateRange.endDate || '2030-12-31');
                    const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

                    // Use the same logic as overview
                    let chartData;
                    if (totalDays >= 365) {
                      // For 12+ months, show monthly rollups
                      chartData = createMonthlyRollups(startDate, endDate, viewsMap);
                    } else if (totalDays > 90) {
                      // For 90+ days, show weekly rollups
                      chartData = createWeeklyRollups(startDate, endDate, viewsMap);
                    } else {
                      // For shorter periods, show daily data
                      chartData = createDailyData(startDate, endDate, viewsMap);
                    }

                    // Always use line chart like the overview
                    const tickRotation = totalDays > 15 ? -60 : -45;

                    return (
                      <ResponsiveLine
                        data={[
                          {
                            id: 'Total Views (selected pages)',
                            data: chartData
                          }
                        ]}
                        margin={{ top: 30, right: 40, bottom: 70, left: 80 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 0 }}
                        curve="monotoneX"
                        enableGridX={false}
                        colors={['#3b82f6']}
                        pointSize={6}
                        enableArea={true}
                        areaOpacity={0.15}
                        animate={false}
                        axisBottom={{
                          tickRotation: tickRotation,
                          tickSize: 5,
                          tickPadding: 5
                        }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5
                        }}
                        tooltip={({ point }) => (
                          <div className="bg-white px-3 py-2 rounded shadow text-gray-800 text-sm font-medium border border-gray-200">
                            <div className="font-semibold mb-1">{point.data.x}</div>
                            <div>Views: {point.data.y}</div>
                          </div>
                        )}
                      />
                    );
                  })()
                ) : (
                  <Box className="flex items-center justify-center h-full">
                    <Typography variant="body2" className="text-gray-500">
                      No data available for the selected period
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Pages Table */}
        <Grid item xs={12} component="div">
          <Paper elevation={0} className="p-4 border border-gray-100">
            <Box className="mb-4" />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      <IconButton size="small" onClick={handleToggleAll}>
                        {allShown ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>Page Path</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'totalViews'}
                        direction={sortConfig.key === 'totalViews' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('totalViews')}
                      >
                        Total Views
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'uniqueUsers'}
                        direction={sortConfig.key === 'uniqueUsers' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('uniqueUsers')}
                      >
                        Unique Users
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'newUsers'}
                        direction={sortConfig.key === 'newUsers' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('newUsers')}
                      >
                        New Users
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'avgEngagementTime'}
                        direction={sortConfig.key === 'avgEngagementTime' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('avgEngagementTime')}
                      >
                        Avg. Engagement
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'bounceRate'}
                        direction={sortConfig.key === 'bounceRate' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('bounceRate')}
                      >
                        Bounce Rate
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'engagedSessions'}
                        direction={sortConfig.key === 'engagedSessions' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('engagedSessions')}
                      >
                        Engaged Sessions
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'eventCount'}
                        direction={sortConfig.key === 'eventCount' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('eventCount')}
                      >
                        Event Count
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Skeleton variant="rectangular" height={400} />
                      </TableCell>
                    </TableRow>
                  ) : sortedPages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" className="text-gray-500 py-4">
                          No pages found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedPages.map((page) => (
                      <TableRow key={page.pagePath}>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={() => handleTogglePage(page.pagePath)}
                            title="Toggle page visibility in chart"
                          >
                            {showPages.includes(page.pagePath) ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://tripinfopages.com${page.pagePath}`, '_blank')}
                              title="Open landing page in new tab"
                              className="mr-1"
                            >
                              <OpenInNewIcon fontSize="small" className="text-gray-500" />
                            </IconButton>
                            <span className="text-gray-900">
                              {page.pagePath}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{page.totalViews}</TableCell>
                        <TableCell align="right">{page.uniqueUsers}</TableCell>
                        <TableCell align="right">{page.newUsers}</TableCell>
                        <TableCell align="right">{page.avgEngagementTime}s</TableCell>
                        <TableCell align="right">{Math.round(page.bounceRate * 100)}%</TableCell>
                        <TableCell align="right">{page.engagedSessions}</TableCell>
                        <TableCell align="right">{page.eventCount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LandingPages; 